import { types as api } from '@mesa-labs/mesa-api';
import ReactApexChart from 'react-apexcharts';
import React, { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import {
  Card,
  CardContainer,
  CardTitle,
  DownloadButton,
  MesaColumn,
  MesaTable,
  ResourceCard,
  ResourceSection,
  useMesaTableServerState,
} from '@mesa-labs/mesa-ui';
import { ResourceItemProp } from '@mesa-labs/mesa-ui/dist/components/ResourceCard';

import {
  exportExternalClientInvoices,
  exportExternalClientVendors,
  useGetExternalClientInvoicesQuery,
  useGetExternalClientQuery,
  useGetExternalClientVendorsQuery,
} from '../../redux/api/externalVendors';
import { useDispatch, useSelector } from '../../redux/hooks';
import {
  updateExternalInvoicesServicedByMesa,
} from '../../redux/slices/externalClients';
import { DefaultPaginationLimits } from './PaginationLimits';
import { asExportFilter, stackedHorizontalBarChartOptions } from '../../utils';
import { ExternalClientVendorMesaColumns } from './ExternalClientVendors';
import MetricStatTable, { MetricGroup } from './MetricStatTable';
import { FilterCell, FilterControls, FilterControlsButtonContainer, FilterRow, FilterSection } from './Filters';
import OptionalBooleanDropdown from './OptionalBooleanDropdown';

const ClientPageContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const ExternalClientInvoiceMesaColumns: MesaColumn<api.ExternalInvoiceDataResponse>[] = [
  {
    key: 'invoiceNumber', label: 'Invoice Number', sortField: 'invoiceNumber', flex: true,
  },
  {
    key: 'invoiceDate', label: 'Invoice Date', type: 'date', sortField: 'invoiceDate',
  },
  {
    key: 'generalLedgerDate', label: 'General Ledger Date', type: 'date', sortField: 'generalLedgerDate',
  },
  {
    key: 'dueDate', label: 'Due Date', type: 'date', sortField: 'dueDate',
  },
  {
    key: 'paymentDate', label: 'Payent Date', type: 'date', sortField: 'paymentDate',
  },
  {
    key: 'grossAmount', label: 'Amount', type: 'currency', sortField: 'grossAmount', currencyCode: 'USD',
  },
  {
    key: 'servicedByMesa', label: 'Service By Mesa', sortField: 'servicedByMesa',
  },
];

type ExternalClientVendorsPageTemplate = {
  businessEntityScope: api.BusinessEntityScope;
};

function ExternalClientPageTemplate(props: ExternalClientVendorsPageTemplate): React.ReactElement | null {
  const { businessEntityScope } = props;
  const { partnerId, id: externalClientId } = useParams();
  const dispatch = useDispatch();
  const externalInvoicesServicedByMesa = useSelector((state) => state.externalClients.externalInvoicesServicedByMesa);
  const partnerIdParam = parseInt(partnerId || '', 10);

  const externalVendorsServer = useMesaTableServerState({
    initialPageSize: 25,
    initialSorting: [{ id: 'totalInvoiceSpend', desc: true }],
    columns: ExternalClientVendorMesaColumns,
    persistKey: 'externalClientPageTemplate-vendors',
  });

  const externalInvoicesServer = useMesaTableServerState({
    initialPageSize: 10,
    initialSorting: [{ id: 'paymentDate', desc: true }],
    columns: ExternalClientInvoiceMesaColumns,
    persistKey: 'externalClientPageTemplate-invoices',
  });

  const {
    data: externalClient,
  } = useGetExternalClientQuery({
    partnerId: partnerIdParam,
    externalClientId: externalClientId!,
    businessEntityScope
  }, { skip: !externalClientId });

  const filter = {
    ...externalVendorsServer.queryArgs,
    sortDirection: externalVendorsServer.queryArgs.sortDirection as api.SortDirection | undefined,
    businessEntityScope,
  };

  const {
    data: {
      data: externalVendors,
      total: totalExternalVendors,
    } = {},
    isLoading: isExternalVendorsLoading,
    isFetching: isExternalVendorsFetching,
  } = useGetExternalClientVendorsQuery({
    partnerId: partnerIdParam,
    externalClientId: externalClientId!,
    ...filter,
  }, { skip: !externalClientId });

  const externalInvoicesFilter = {
    ...externalInvoicesServer.queryArgs,
    sortDirection: externalInvoicesServer.queryArgs.sortDirection as api.SortDirection | undefined,
    partnerId: partnerIdParam,
    externalClientId: externalClientId!,
    businessEntityScope,
    servicedByMesa: externalInvoicesServicedByMesa,
  };
  const {
    data: {
      data: externalInvoices,
      total: totalExternalInvoices,
    } = {},
    isLoading: isExternalInvoicesLoading,
    isFetching: isExternalInvoicesFetching,
  } = useGetExternalClientInvoicesQuery(externalInvoicesFilter, { skip: !externalClientId });

  const timelineSeries: ApexAxisChartSeries = useMemo(() => {
    if (!externalClient) {
      return [];
    }

    return [
      {
        name: 'Weighted Average Approval Time',
        data: [{ x: 'Time (Days)', y: externalClient.weightedAverageApprovalTime }]
      },
      {
        name: 'Weighted Average Import Lateness',
        data: [{ x: 'Time (Days)', y: externalClient.weightedAverageImportLateness }]
      },
      {
        name: 'Weighted Average Pre-Float Period',
        data: [{ x: 'Time (Days)', y: externalClient.weightedAveragePreFloatPeriod }]
      },
      {
        name: 'Weighted Average Float Period',
        data: [{ x: 'Time (Days)', y: externalClient.weightedAverageFloatPeriod }]
      },
    ];
  }, [externalClient]);

  const vendorRestrictions = useMemo(() => externalClient?.vendorRestrictions?.map(vr => `${vr.externalVendorId} (${vr.restrictionType})`).join(','), [externalClient]);

  if (!externalClient) {
    return null;
  }

  const externalClientColumns: Array<[string, Array<string | ResourceItemProp>]> =
    [
      ['Basic Info',
        [
          { key: 'partnerName', label: 'Partner' },
          { key: 'externalClientId', label: 'External Client Id' },
          { key: 'defaultPaymentTermsDays', label: 'Default Payment Terms' },
          { key: 'segment', label: 'Client Segment' },
          { key: 'division', label: 'Division' },
          { key: 'exclusionType', label: 'Exclusion Type' },
          { key: 'exclusionReason', label: 'Exclusion Reason' },
          { key: 'vendorRestrictions', label: 'Vendor Restrictions' },
        ]
      ],
      ['POA Qualification',
        [
          { key: 'exclusionType', label: 'Exclusion Type' },
          { key: 'exclusionReason', label: 'Exclusion Reason' },
          { key: 'poaPriceTier', label: 'POA Price Tier', type: 'percentage' },
          { key: 'poaApr', label: 'POA APR', type: 'percentage' },
        ]
      ],
    ];

  const invoiceVolumeGroups: MetricGroup[] = [
    {
      label: 'Counts',
      rows: [
        { label: 'Eligible Invoices', t6m: externalClient.eligibleInvoiceCountT6m, t12m: externalClient.eligibleInvoiceCountT12m, format: 'count' },
        { label: 'Invoices Accelerated', allTime: externalClient.invoicesAccelerated, format: 'count' },
        { label: 'Total Vendors', allTime: externalClient.totalVendors, format: 'count' },
        { label: 'Signed Vendors', allTime: externalClient.totalInternalVendors, format: 'count' },
        { label: 'Funded Vendors', t12m: externalClient.totalServicedVendorsT12m, format: 'count' },
      ],
    },
    {
      label: 'Spend',
      rows: [
        { label: 'Total Invoice Spend', t12m: externalClient.totalInvoiceSpendT12m, format: 'currency' },
        { label: 'Total Signed Invoice Spend', t12m: externalClient.totalInternalInvoiceSpendT12m, format: 'currency' },
        { label: 'Total Funded Invoice Spend', t12m: externalClient.totalServicedInvoiceSpendT12m, format: 'currency' },
        { label: 'Monthly Spend', t6m: externalClient.monthlyInvoiceSpendT6m, t12m: externalClient.monthlyInvoiceSpendT12m, format: 'currency' },
        { label: 'Monthly Spend Std Dev', t6m: externalClient.monthlyInvoiceSpendStdDevT6m, t12m: externalClient.monthlyInvoiceSpendStdDevT12m, format: 'currency' },
        { label: 'Amount Accelerated', allTime: externalClient.invoiceAmountAccelerated, format: 'currency' },
      ],
    },
    {
      label: 'Revenue',
      rows: [
        { label: 'Average Monthly Revenue', t6m: externalClient.averageMonthlyRevenueT6m, t12m: externalClient.averageMonthlyRevenueT12m, format: 'currency' },
        { label: 'Total Revenue', t6m: externalClient.totalRevenueT6m, t12m: externalClient.totalRevenueT12m, format: 'currency' },
        { label: 'All-Time Revenue', allTime: externalClient.revenueGenerated, format: 'currency' },
      ],
    },
  ];

  const timelineGroups: MetricGroup[] = [
    {
      label: 'Weighted Averages',
      rows: [
        { label: 'Payment Tenor (WAPT)', t6m: externalClient.weightedAveragePaymentTenorT6m, t12m: externalClient.weightedAveragePaymentTenorT12m, format: 'days' },
        { label: 'Ledger Lateness (WALL)', t6m: externalClient.weightedAverageLedgerLatenessT6m, t12m: externalClient.weightedAverageLedgerLatenessT12m, format: 'days' },
      ],
    },
    {
      label: 'Internal Pipeline',
      rows: [
        { label: 'Approval Time', allTime: externalClient.weightedAverageApprovalTime, format: 'days' },
        { label: 'Import Lateness', allTime: externalClient.weightedAverageImportLateness, format: 'days' },
        { label: 'Pre-Float Period', allTime: externalClient.weightedAveragePreFloatPeriod, format: 'days' },
        { label: 'Float Period', allTime: externalClient.weightedAverageFloatPeriod, format: 'days' },
      ],
    },
  ];

  const yieldRiskGroups: MetricGroup[] = [
    {
      label: 'Conversion',
      rows: [
        { label: 'Vendor Conversion', allTime: externalClient.internalVendorsConversion, format: 'percentage' },
        { label: 'Invoice Spend Conversion', t12m: externalClient.internalInvoiceSpendConversionT12m, format: 'percentage' },
      ],
    },
    {
      label: 'Pricing',
      rows: [
        { label: 'Net 30 APR', t6m: externalClient.weightedEligibleAverageNet30AprT6m, t12m: externalClient.weightedEligibleAverageNet30AprT12m, format: 'percentage' },
        { label: 'Net 30 Yield Pricing', t6m: externalClient.weightedEligibleAverageNet30YieldPricingT6m, t12m: externalClient.weightedEligibleAverageNet30YieldPricingT12m, format: 'percentage' },
        { label: 'Weighted Discount Rate', allTime: externalClient.weightedAverageDiscountRate, format: 'percentage' },
        { label: 'Weighted Average APR', allTime: externalClient.weightedAverageApr, format: 'percentage' },
      ],
    },
    {
      label: 'Invoice Failure Rates',
      rows: [
        { label: 'Internal Invoices Failing Net-30', allTime: externalClient.internalPercentageFailingNet30, format: 'percentage' },
        { label: 'Internal Invoices Failing Net-60', allTime: externalClient.internalPercentageFailingNet60, format: 'percentage' },
        { label: 'Internal Invoices Settled In 3', allTime: externalClient.internalPercentageSettledWithin3, format: 'percentage' },
        { label: 'External Invoices Failing Net-30', allTime: externalClient.externalPercentageFailingNet30, format: 'percentage' },
        { label: 'External Invoices Failing Net-60', allTime: externalClient.externalPercentageFailingNet60, format: 'percentage' },
        { label: 'External Invoices Settled In 3', allTime: externalClient.externalPercentageSettledWithin3, format: 'percentage' },
      ],
    },
  ];

  return (
    <ClientPageContainer>
      <CardContainer>
        <ResourceCard
          resource={{
            ...externalClient,
            partnerName: api.getPartnerName(externalClient.partnerId),
            vendorRestrictions,
            currency: 'USD', // TODO: MESA-1457: Multi-currency support
          }}
          title={externalClient.externalClientName || externalClient.externalClientId}
          items={[]}
          sectionedItems={externalClientColumns}
          columns={4}
        />
      </CardContainer>
      <MetricStatTable persistKey="externalClientInvoiceVolumeSpend" title="Invoice Volume & Spend" groups={invoiceVolumeGroups} />
      <MetricStatTable persistKey="externalClientPaymentTimeline" title="Payment Timeline" subtitle="Weighted averages · days" groups={timelineGroups} />
      <MetricStatTable persistKey="externalClientConversionYieldRisk" title="Conversion, Yield & Risk" groups={yieldRiskGroups} />
      <CardContainer>
        <Card minHeight="400px">
          <CardTitle>Weighted Average Invoice Timeline</CardTitle>
          <ReactApexChart options={stackedHorizontalBarChartOptions()} series={timelineSeries} type="bar"
            height={350} />
        </Card>
      </CardContainer>
      <ResourceSection
        title="External Vendors (T12M)"
        controls={(
          <DownloadButton
            text="Download CSV"
            data={() => exportExternalClientVendors(partnerIdParam, externalClient.externalClientId, {
              ...asExportFilter(filter),
            })}
            fileName="external-client-vendors.csv"
            display="purple"
          />
        )}
      >
        <MesaTable
          columns={ExternalClientVendorMesaColumns}
          data={externalVendors || []}
          dynamicColumnMode
          getRowId={(row) => `${row.partnerId}:${row.externalClientId}:${row.externalVendorId}`}
          isLoading={isExternalVendorsLoading || isExternalVendorsFetching}
          {...externalVendorsServer.bind}
          pagination={{
            ...externalVendorsServer.bind.pagination,
            total: totalExternalVendors,
            pageSizeOptions: DefaultPaginationLimits.map((l) => l.value),
          }}
          columnLayout={{ enableResizing: true, enableVisibilityToggle: true, enablePinning: true }}
          minColumnWidth={90}
        />
      </ResourceSection>

      <ResourceSection
        title="Invoices"
      >
        <FilterSection
          filterControl={(
            <FilterControls>
              <FilterControlsButtonContainer>
                <DownloadButton
                  text="Download CSV"
                  data={() => exportExternalClientInvoices(partnerIdParam, externalClientId!, {
                    ...asExportFilter(externalInvoicesFilter),
                  })}
                  fileName={`external-client-${externalClientId}-invoices.csv`}
                  display="purple"
                />
              </FilterControlsButtonContainer>
            </FilterControls>
          )}
        >
          {businessEntityScope !== api.BusinessEntityScope.Internal ? (
            <FilterRow columns={4}>
              <FilterCell>
                <OptionalBooleanDropdown
                  label="Serviced By Mesa"
                  selectedOption={externalInvoicesServicedByMesa}
                  onChange={(value) => dispatch(updateExternalInvoicesServicedByMesa(value))}
                />
              </FilterCell>
            </FilterRow>
          ) : (<></>)}
        </FilterSection>

        <MesaTable
          columns={ExternalClientInvoiceMesaColumns}
          data={externalInvoices || []}
          dynamicColumnMode
          getRowId={(row) => `${row.partnerId}:${row.externalClientId}:${row.externalDocumentId}:${row.documentType}`}
          isLoading={isExternalInvoicesLoading || isExternalInvoicesFetching}
          {...externalInvoicesServer.bind}
          pagination={{
            ...externalInvoicesServer.bind.pagination,
            total: totalExternalInvoices,
            pageSizeOptions: DefaultPaginationLimits.map((l) => l.value),
          }}
          columnLayout={{ enableResizing: true, enableVisibilityToggle: true, enablePinning: true }}
          minColumnWidth={90}
        />
      </ResourceSection>
    </ClientPageContainer>
  );
}

export default ExternalClientPageTemplate;
